import Badge from "@/components/core/badge";
import { Button } from "@/components/core/buttons/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/core/card";
import { Content } from "@/components/layouts/content";
import { useContractInvoicesQuery } from "@/features/dashboard/api/queries/contract-invoice-query";
import { useSalesContractQuery } from "@/features/dashboard/api/queries/sales-contract-query";
import { CONTRACT_TYPE_LABELS, PROJECT_TYPE_LABELS } from "@/features/dashboard/types/sales-contract";
import type { SalesContract } from "@/features/dashboard/types/sales-contract";
import { formatDate } from "@/utils/format-date";
import { formatNumber } from "@/utils/format-number";
import { IconArrowLeft, IconBuilding, IconCalendar, IconCurrencyPeso, IconFileInvoice, IconUsers } from "@tabler/icons-react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";

export const Route = createFileRoute("/_dashboard/sales-contracts_/$contractId")({
	component: Page,
});

function Page() {
	const { contractId } = Route.useParams();
	const navigate = useNavigate();

	const { data: contract, isLoading } = useSalesContractQuery(contractId);

	if (isLoading) {
		return (
			<Content pageName="Contract Details">
				<div className="flex items-center justify-center h-64">
					<div className="text-center">
						<div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto" />
						<p className="mt-2 text-sm text-gray-600">Loading contract details...</p>
					</div>
				</div>
			</Content>
		);
	}

	if (!contract) {
		return (
			<Content pageName="Contract Details">
				<div className="flex items-center justify-center h-64">
					<div className="text-center">
						<p className="text-lg text-gray-600">Contract not found</p>
					</div>
				</div>
			</Content>
		);
	}

	const formatCurrency = (value: number) => {
		return new Intl.NumberFormat("en-PH", {
			style: "currency",
			currency: "PHP",
			minimumFractionDigits: 0,
			maximumFractionDigits: 0,
		}).format(value);
	};

	const getStatusBadge = (status: string) => {
		const variantMap: Record<string, string> = {
			active: "green",
			expired: "red",
			cancelled: "yellow",
			completed: "blue",
		};
		return (
			<Badge color={variantMap[status] || "gray"}>
				{status?.charAt(0).toUpperCase()}
				{status?.slice(1)}
			</Badge>
		);
	};

	return (
		<Content pageName={`Contract: ${contract.project?.name || "Details"}`}>
			<div className="p-6 space-y-6">
				{/* Header */}
				<div className="flex items-start justify-between">
					<div className="flex items-center gap-4">
						<Button variant="outline" size="sm" onClick={() => navigate({ to: "/sales-contracts" })}>
							<IconArrowLeft className="w-4 h-4 mr-1" />
							Back
						</Button>
						<div>
							<h1 className="text-2xl font-bold text-gray-900">{contract.project?.name}</h1>
							<p className="text-sm text-gray-500 mt-1">{contract.project?.client?.company || contract.project?.client?.name || "No client"}</p>
						</div>
					</div>
					<div className="flex items-center gap-2">
						{getStatusBadge(contract.status)}
						{contract.project_type && (
							<Badge color={contract.project_type === "revlv_project" ? "purple" : "orange"}>{PROJECT_TYPE_LABELS[contract.project_type]}</Badge>
						)}
						{contract.is_extended && <Badge color="yellow">Extended</Badge>}
					</div>
				</div>

				{/* Info cards */}
				<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
					<InfoCard icon={<IconBuilding className="w-5 h-5" />} label="Project" value={contract.project?.name || "-"} />
					<InfoCard icon={<IconUsers className="w-5 h-5" />} label="Salesperson" value={contract.salesperson?.name || "Not assigned"} />
					<InfoCard
						icon={<IconCalendar className="w-5 h-5" />}
						label="Period"
						value={`${formatDate(contract.start_date)} - ${formatDate(contract.effective_end_date || contract.end_date)}`}
					/>
					<InfoCard icon={<IconCurrencyPeso className="w-5 h-5" />} label="Contract Amount" value={formatCurrency(contract.contract_amount)} />
					<InfoCard icon={<IconCurrencyPeso className="w-5 h-5" />} label="Monthly Recurring" value={formatCurrency(contract.monthly_recurring_sales)} />
				</div>

				{/* Detail cards */}
				<div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
					<Card>
						<CardHeader>
							<CardTitle className="text-lg">Contract Information</CardTitle>
						</CardHeader>
						<CardContent>
							<div className="space-y-4">
								<DetailItem label="Project" value={contract.project?.name || "-"} />
								<DetailItem label="Client" value={contract.project?.client?.company || contract.project?.client?.name || "-"} />
								<DetailItem label="Salesperson" value={contract.salesperson?.name || "-"} />
								<DetailItem label="Salesperson Email" value={contract.salesperson?.email || "-"} />
								<DetailItem label="Status" value={contract.status} />
								<DetailItem
									label="Payment Type"
									value={contract.payment_type ? contract.payment_type.charAt(0).toUpperCase() + contract.payment_type.slice(1) : "-"}
								/>
								<DetailItem label="Contract Type" value={contract.contract_type ? CONTRACT_TYPE_LABELS[contract.contract_type] : "-"} />
							</div>
						</CardContent>
					</Card>

					<Card>
						<CardHeader>
							<CardTitle className="text-lg">Dates & Duration</CardTitle>
						</CardHeader>
						<CardContent>
							<div className="space-y-4">
								<DetailItem label="Start Date" value={formatDate(contract.start_date)} />
								<DetailItem label="End Date" value={formatDate(contract.end_date)} />
								{contract.is_extended && <DetailItem label="Extended End Date" value={formatDate(contract.extended_end_date || "")} />}
								<DetailItem label="Months Remaining" value={contract.months_remaining?.toString() || "0"} />
								<DetailItem label="Number of Invoices" value={contract.number_of_invoices?.toString() || "-"} />
								<DetailItem label="VATable" value={contract.is_vatable ? "Yes" : "No"} />
							</div>
						</CardContent>
					</Card>

					<Card>
						<CardHeader>
							<CardTitle className="text-lg">Financial Details</CardTitle>
						</CardHeader>
						<CardContent>
							<div className="space-y-4">
								<DetailItem label="Contract Amount" value={formatCurrency(contract.contract_amount)} />
								<DetailItem label="Monthly Recurring Sales" value={formatCurrency(contract.monthly_recurring_sales)} />
								{contract.royalty_amount && <DetailItem label="Royalty Amount" value={formatCurrency(contract.royalty_amount)} />}
								{contract.original_contract_amount && <DetailItem label="Original Contract Amount" value={formatCurrency(contract.original_contract_amount)} />}
								{contract.original_monthly_recurring_sales && (
									<DetailItem label="Original Monthly Recurring" value={formatCurrency(contract.original_monthly_recurring_sales)} />
								)}
							</div>
						</CardContent>
					</Card>
				</div>

				{/* Tags */}
				{contract.tags && contract.tags.length > 0 && (
					<Card>
						<CardHeader>
							<CardTitle className="text-lg">Tags</CardTitle>
						</CardHeader>
						<CardContent>
							<div className="flex flex-wrap gap-2">
								{contract.tags.map((tag, index) => (
									<span key={index} className="inline-flex items-center rounded-full bg-blue-100 text-blue-800 px-3 py-1 text-sm font-medium">
										{tag}
									</span>
								))}
							</div>
						</CardContent>
					</Card>
				)}

				{/* Notes */}
				{contract.notes && (
					<Card>
						<CardHeader>
							<CardTitle className="text-lg">Notes</CardTitle>
						</CardHeader>
						<CardContent>
							<p className="text-sm text-gray-700 whitespace-pre-wrap">{contract.notes}</p>
						</CardContent>
					</Card>
				)}

				{/* Invoices */}
				<ContractInvoicesSection contractId={contractId} contract={contract} />
			</div>
		</Content>
	);
}

function ContractInvoicesSection({ contractId, contract }: { contractId: string; contract: SalesContract }) {
	const { data: invoicesData, isLoading } = useContractInvoicesQuery(contractId, "", "");

	const invoices = invoicesData?.data || [];

	return (
		<Card>
			<CardHeader>
				<CardTitle className="flex items-center gap-2">
					<IconFileInvoice className="w-5 h-5" />
					Invoices ({invoices.length})
				</CardTitle>
			</CardHeader>
			<CardContent>
				{isLoading ? (
					<div className="flex items-center justify-center h-32">
						<div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
						<span className="ml-2 text-sm text-gray-600">Loading invoices...</span>
					</div>
				) : invoices.length === 0 ? (
					<div className="text-center py-8 text-gray-500">
						<IconFileInvoice className="w-12 h-12 mx-auto mb-2 text-gray-300" />
						<p>No invoices found for this contract.</p>
					</div>
				) : (
					<div className="overflow-x-auto">
						<table className="w-full">
							<thead>
								<tr className="border-b border-gray-200">
									<th className="text-left px-4 py-2 text-xs font-medium text-gray-500 uppercase">Invoice #</th>
									<th className="text-left px-4 py-2 text-xs font-medium text-gray-500 uppercase">Description</th>
									<th className="text-left px-4 py-2 text-xs font-medium text-gray-500 uppercase">Amount</th>
									<th className="text-left px-4 py-2 text-xs font-medium text-gray-500 uppercase">Date</th>
									<th className="text-left px-4 py-2 text-xs font-medium text-gray-500 uppercase">Due Date</th>
									<th className="text-left px-4 py-2 text-xs font-medium text-gray-500 uppercase">Collection Status</th>
								</tr>
							</thead>
							<tbody>
								{invoices.map((invoice: Record<string, unknown>) => (
									<tr key={invoice.id as string} className="border-b border-gray-100 hover:bg-gray-50">
										<td className="px-4 py-3 text-sm font-medium">{(invoice.invoice_number as string) || "-"}</td>
										<td className="px-4 py-3 text-sm">{(invoice.description as string) || "-"}</td>
										<td className="px-4 py-3 text-sm font-medium">{formatNumber(invoice.amount as number)}</td>
										<td className="px-4 py-3 text-sm">{formatDate(invoice.invoice_date as string)}</td>
										<td className="px-4 py-3 text-sm">{formatDate(invoice.due_date as string)}</td>
										<td className="px-4 py-3 text-sm">
											<Badge
												color={
													(invoice.collection_status as string) === "collected"
														? "green"
														: (invoice.collection_status as string) === "partial"
															? "blue"
															: (invoice.collection_status as string) === "pending"
																? "yellow"
																: "gray"
												}
											>
												{(invoice.collection_status as string) || "pending"}
											</Badge>
										</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>
				)}
			</CardContent>
		</Card>
	);
}

function InfoCard({ icon, label, value }: { icon: React.ReactNode; label: string; value: string }) {
	return (
		<Card>
			<CardContent className="p-4">
				<div className="flex items-center gap-3">
					<div className="text-gray-500">{icon}</div>
					<div>
						<p className="text-xs text-gray-500">{label}</p>
						<p className="text-sm font-semibold text-gray-900">{value}</p>
					</div>
				</div>
			</CardContent>
		</Card>
	);
}

function DetailItem({ label, value }: { label: string; value: string }) {
	return (
		<div>
			<p className="text-xs text-gray-500 font-medium">{label}</p>
			<p className="text-sm font-medium text-gray-900 mt-1">{value}</p>
		</div>
	);
}
